import "./style/app-root.scss"

import React, { useEffect } from "react"
import { useNativeTabBarTab } from "@app/helpers/nativeTabBar/useNativeTabBarTab"
import { useSelector, useDispatch } from "react-redux"
import { LocalStorage } from "@ng-mw/shared-react-components"
import MenyLocalStorage from "@app/MenyLocalStorage"
import { RootState } from "@app/types"
import { showAppOnboardingPoster, showNewsPopup } from "@app/store/app/appActions"
import { PushNotificationAndroidDrawer } from "@ng-mw/shared-react-components/push-notifications"
import AppGlobalSettings from "@app/AppGlobalSettings"
import { getNativeTabBarInsets } from "@app/helpers/nativeTabBar/nativeTabBarInsets"

// Components
import AppLayoutWrapper from "./AppLayoutWrapper"
import AppNavigation from "./AppNavigation"
import Popups from "./Popups"
import { NewsConfig } from "@modules/onboarding/const/newsConfig"
import { useNativeTabBarBadge } from "@app/helpers/nativeTabBar/useNativeTabBarBadge"
import { useShouldUseNativeTabBar } from "@app/helpers/nativeTabBar/useShouldUseNativeTabBar"
import NativeTabBarOverlayGuard from "./NativeTabBarOverlayGuard"
import AppNetworkStatus from "./AppNetworkStatus"
import AppVersionStatus from "./AppVersionStatus"

const AppRoot = () => {
    const dispatch = useDispatch<any>()
    const safeArea = useSelector((state: RootState) => state.native.safeArea)
    const { osVersion, os, supportsNativeTabs } = useSelector(
        (state: RootState) => state.appEnvConfig,
    )
    const userIsCorporate = useSelector(
        (state: RootState) => state.user.data.household.isOrganization,
    )
    const userIsLoggedIn = useSelector(
        (state: RootState) => state.user.loginLevel && state.user.data.chainId,
    )
    const isUserLoaded = useSelector((state: RootState) => !!state.user.data.chainId)

    const shouldUseNativeTabBar = useShouldUseNativeTabBar(supportsNativeTabs)
    const { bottomObstruction, scrollBottomInset } = getNativeTabBarInsets({
        isNative: AppGlobalSettings.isNative(),
        supportsNativeTabs: supportsNativeTabs,
        os,
        osVersion,
        bottomObstruction: safeArea.bottomObstruction,
    })

    const isIos26OrAbove = os === "ios" && Number.parseInt(osVersion ?? "0", 10) >= 26

    useNativeTabBarTab()
    useNativeTabBarBadge()

    useEffect(() => {
        const today = new Date()
        if (isUserLoaded && !userIsCorporate) {
            if (!userIsLoggedIn && !LocalStorage.get(MenyLocalStorage.UserSeenNewOnboarding)) {
                dispatch(showAppOnboardingPoster())
            }

            if (userIsLoggedIn) {
                NewsConfig.forEach(news => {
                    if (!LocalStorage.get(news.localStorageKey)) {
                        if (news.validFrom && today > news.validFrom) {
                            dispatch(showNewsPopup(news))
                        }
                    }
                })
            }
        }
    }, [userIsCorporate, isUserLoaded, userIsLoggedIn, dispatch])

    useEffect(() => {
        document.body.style.setProperty("--safe-area-top", `${safeArea.top}px`)
        document.body.style.setProperty("--safe-area-bottom", `${safeArea.bottom}px`)
    }, [safeArea.bottom, safeArea.top])

    return (
        <div
            className={
                shouldUseNativeTabBar
                    ? `app-root-native-tabs${isIos26OrAbove ? " app-root-native-tabs--ios26" : ""}`
                    : "app-root"
            }
            style={{
                "--base-height": "100%",
                "--safe-area-top": `${safeArea.top}px`,
                "--safe-area-bottom": `${safeArea.bottom}px`,
                "--native-bottom-overlay-inset": `${bottomObstruction}px`,
                "--native-scroll-bottom-inset": `${scrollBottomInset}px`,
            }}
        >
            <AppLayoutWrapper> {shouldUseNativeTabBar ? null : <AppNavigation />}</AppLayoutWrapper>
            <AppVersionStatus />
            <AppNetworkStatus />
            <NativeTabBarOverlayGuard />
            <Popups showNativeDebugLink={shouldUseNativeTabBar} />
            <PushNotificationAndroidDrawer
                androidApiLevel={parseInt(osVersion ?? "-1", 10)}
                isAndroid={os === "android"}
            />
        </div>
    )
}

export default AppRoot
